import BaseController from '..';
import IAudit from './IAudit';
import {
    AuditLogFilters,
    AuditLogsResponse,
    EntityAuditResponse,
    AuditStatisticsResponse,
    GroupedAuditLogsResponse
} from '../../../models/entities/audit/auditLog';

const DEFAULT_AUDIT_API_BASE = '/admin/audit';
const PLATFORM_AUDIT_API_BASE = '/super-admin/audit';

export class AuditController extends BaseController implements IAudit {
    constructor(baseUrl: string) {
        super(baseUrl);
    }

    private resolveBase(usePlatformAudit?: boolean): string {
        return usePlatformAudit ? PLATFORM_AUDIT_API_BASE : DEFAULT_AUDIT_API_BASE;
    }

    private buildQueryParams(params: Record<string, any>): URLSearchParams {
        const searchParams = new URLSearchParams();
        
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    searchParams.append(key, value.join(','));
                } else {
                    searchParams.append(key, String(value));
                }
            }
        });
        
        return searchParams;
    }

    async fetchAuditLogs(filters: AuditLogFilters = {}, usePlatformAudit = false): Promise<AuditLogsResponse> {
        const base = this.resolveBase(usePlatformAudit);
        const params = this.buildQueryParams(filters);
        const response = await this.client.get(`${base}?${params}`);
        return response;
    }

    async fetchGroupedAuditLogs(filters: AuditLogFilters = {}, usePlatformAudit = false): Promise<GroupedAuditLogsResponse> {
        const base = this.resolveBase(usePlatformAudit);
        const params = this.buildQueryParams(filters);
        const response = await this.client.get(`${base}/grouped?${params}`);
        return response;
    }

    async searchAuditLogs(query: string, filters: AuditLogFilters = {}, usePlatformAudit = false): Promise<AuditLogsResponse> {
        const base = this.resolveBase(usePlatformAudit);
        const params = this.buildQueryParams({ q: query, ...filters });
        const response = await this.client.get(`${base}/search?${params}`);
        return response;
    }

    async fetchEntityAudit(entityType: string, entityId: string, limit: number = 100): Promise<EntityAuditResponse> {
        const params = this.buildQueryParams({ limit });
        const response = await this.client.get(
            `${DEFAULT_AUDIT_API_BASE}/entity/${entityType}/${entityId}?${params}`
        );
        return response;
    }

    async fetchAuditStatistics(filters: Omit<AuditLogFilters, 'limit' | 'offset'> = {}): Promise<AuditStatisticsResponse> {
        const params = this.buildQueryParams(filters);
        const response = await this.client.get(`${DEFAULT_AUDIT_API_BASE}/statistics?${params}`);
        return response;
    }

    async exportAuditData(filters: AuditLogFilters = {}, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
        const params = this.buildQueryParams({ ...filters, format });
        const response = await this.client.get(`${DEFAULT_AUDIT_API_BASE}/export?${params}`);
        return response as unknown as Blob;
    }

    async fetchEntityTypes(usePlatformAudit = false): Promise<{ success: boolean; data: string[] }> {
        const base = this.resolveBase(usePlatformAudit);
        const response = await this.client.get(`${base}/metadata/entity-types`);
        return response;
    }

    async fetchAuditUsers(companyId?: string, usePlatformAudit = false): Promise<{ success: boolean; data: Array<{ userId: string; userName: string }> }> {
        const base = this.resolveBase(usePlatformAudit);
        const params = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
        const response = await this.client.get(`${base}/metadata/users${params}`);
        return response;
    }
}